import { useState, useCallback } from 'react';

const useConversationState = () => {
    const [conversationId, setConversationId] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('conversationId') || null;
    });
    const [followUpQuestions, setFollowUpQuestions] = useState([]);
    const [conversationHistory, setConversationHistory] = useState([]);
    const [isActivelyStreaming, setIsActivelyStreaming] = useState(false);
    const [streamingAIResponse, setStreamingAIResponse] = useState('');
    const [currentStatus, setCurrentStatus] = useState('');
    const [previewSearchQueries, setPreviewSearchQueries] = useState([]);
    const [previewQueryProducts, setPreviewQueryProducts] = useState([]);
    const [isWaitingForFollowUpQueries, setIsWaitingForFollowUpQueries] = useState(false);
    // Centralized product metadata storage - maps stacklineSku to full product metadata
    const [productMetadataMap, setProductMetadataMap] = useState(new Map());
    // Centralized survey metadata storage - maps campaignId to full survey metadata
    const [surveyMetadataMap, setSurveyMetadataMap] = useState(new Map());
    // Store product group SKUs for streaming (maps group index to array of SKUs)
    const [streamingProductGroups, setStreamingProductGroups] = useState(new Map());
    // Store featured product being streamed (shown above user message during streaming)
    const [streamingFeaturedProduct, setStreamingFeaturedProduct] = useState(null);
    // Store store locator being streamed (shown during streaming)
    const [streamingStoreLocator, setStreamingStoreLocator] = useState(null);
    // Store login required data being streamed (shown during streaming)
    const [streamingLoginRequired, setStreamingLoginRequired] = useState(null);
    // Store devInfo (devOptions used + effective ranking weights) during streaming
    const [streamingDevInfo, setStreamingDevInfo] = useState(null);
    /** Page context where the current conversation was started (used to reset when opening widget on a different page) */
    const [conversationPageContext, setConversationPageContext] = useState(null);
    // ---- Sharing state ----
    /** True when viewing a shared thread that the current user does not own (pre-fork) */
    const [isSharedView, setIsSharedView] = useState(false);
    /** Display name of the user who shared this thread */
    const [sharedByUserName, setSharedByUserName] = useState(undefined);
    /** The checkpoint message index from the shared view (used during fork to set sourceCheckpointIndex) */
    const [sharedAtMessageIndex, setSharedAtMessageIndex] = useState(undefined);
    /** The original conversation ID this forked thread was copied from */
    const [sourceConversationId, setSourceConversationId] = useState(undefined);
    /** The message index boundary between copied messages and the user's new messages in a forked thread */
    const [sourceCheckpointIndex, setSourceCheckpointIndex] = useState(undefined);
    // Clear all streaming-related state (called when stream completes)
    const clearStreamingState = useCallback(() => {
        setStreamingAIResponse('');
        setPreviewSearchQueries([]);
        setPreviewQueryProducts([]);
        setCurrentStatus('');
        setStreamingFeaturedProduct(null);
        setStreamingStoreLocator(null);
        setStreamingLoginRequired(null);
        setStreamingDevInfo(null);
    }, []);
    return {
        conversationId,
        setConversationId,
        conversationHistory,
        setConversationHistory,
        isActivelyStreaming,
        setIsActivelyStreaming,
        streamingAIResponse,
        setStreamingAIResponse,
        currentStatus,
        setCurrentStatus,
        previewSearchQueries,
        setPreviewSearchQueries,
        previewQueryProducts,
        setPreviewQueryProducts,
        isWaitingForFollowUpQueries,
        setIsWaitingForFollowUpQueries,
        productMetadataMap,
        setProductMetadataMap,
        surveyMetadataMap,
        setSurveyMetadataMap,
        streamingProductGroups,
        setStreamingProductGroups,
        followUpQuestions,
        setFollowUpQuestions,
        streamingFeaturedProduct,
        setStreamingFeaturedProduct,
        streamingStoreLocator,
        setStreamingStoreLocator,
        streamingLoginRequired,
        setStreamingLoginRequired,
        streamingDevInfo,
        setStreamingDevInfo,
        clearStreamingState,
        conversationPageContext,
        setConversationPageContext,
        // Sharing state
        isSharedView,
        setIsSharedView,
        sharedByUserName,
        setSharedByUserName,
        sharedAtMessageIndex,
        setSharedAtMessageIndex,
        sourceConversationId,
        setSourceConversationId,
        sourceCheckpointIndex,
        setSourceCheckpointIndex,
    };
};

export { useConversationState };
//# sourceMappingURL=useConversationState.js.map
